import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../services/api';
import { useApp } from '../context/AppContext';
import { Badge } from '../components/Badge';
import { formatCents, formatCurrencyARS, formatDate } from '../utils/format';
import { PF_TX_TYPE_VALUES, enumLabel } from '../utils/i18nLabels';

const emptyTx = {
  type: 'expense',
  amount_cents: 5000,
  currency: 'ARS',
  category: '',
  description: '',
  transaction_date: new Date().toISOString().slice(0, 10),
  is_recurring: false,
};

const PF_TAB_IDS = ['summary', 'budget', 'movements', 'goals', 'recurring', 'debts', 'projection', 'reco'];

const PF_TAB_I18N = {
  summary: 'pfTabSummary',
  budget: 'pfTabBudget',
  movements: 'pfTabMovements',
  goals: 'pfTabGoals',
  recurring: 'pfTabRecurring',
  debts: 'pfTabDebts',
  projection: 'pfTabProjection',
  reco: 'pfTabReco',
};

const BUDGET_WEIGHTS = {
  housing: 0.28,
  food: 0.14,
  transport: 0.12,
  health: 0.08,
  education: 0.06,
  services: 0.1,
  debts: 0.1,
  leisure: 0.08,
  savings: 0.12,
  other: 0.06,
};

const BUDGET_KEYS = Object.keys(BUDGET_WEIGHTS);

const PF_BUDGET_LS_LEGACY = 'spectra-rrhh-pf-budget-v2';
const PF_BUDGET_LS = 'spectra-rrhh-pf-budget-v3-pesos';

function defaultBudgetCapsFromIncome(incomeCents) {
  const next = {};
  for (const k of BUDGET_KEYS) {
    next[k] = Math.round((incomeCents * (BUDGET_WEIGHTS[k] || 0.06)) / 100);
  }
  return next;
}

function loadBudgetFromStorage(incomeCents) {
  const base = defaultBudgetCapsFromIncome(incomeCents);
  try {
    const v3 = localStorage.getItem(PF_BUDGET_LS);
    if (v3) {
      const o = JSON.parse(v3);
      if (o.caps && typeof o.caps === 'object') {
        const merged = { ...base };
        BUDGET_KEYS.forEach((k) => {
          if (typeof o.caps[k] === 'number' && !Number.isNaN(o.caps[k])) merged[k] = o.caps[k];
        });
        const extras = Array.isArray(o.extras)
          ? o.extras
              .filter((x) => x && x.id && x.label)
              .map((x) => ({
                id: x.id,
                label: x.label,
                capPesos:
                  typeof x.capPesos === 'number' && !Number.isNaN(x.capPesos)
                    ? Math.round(x.capPesos)
                    : Math.round((Number(x.capCents) || 0) / 100),
              }))
          : [];
        return { caps: merged, extras };
      }
    }
    const leg = localStorage.getItem(PF_BUDGET_LS_LEGACY);
    if (leg) {
      const o = JSON.parse(leg);
      const merged = { ...base };
      if (o.caps && typeof o.caps === 'object') {
        BUDGET_KEYS.forEach((k) => {
          if (typeof o.caps[k] === 'number' && !Number.isNaN(o.caps[k])) {
            merged[k] = Math.round(o.caps[k] / 100);
          }
        });
      }
      const extras = Array.isArray(o.extras)
        ? o.extras
            .filter((x) => x && x.id && x.label)
            .map((x) => ({
              id: x.id,
              label: x.label,
              capPesos: Math.round((Number(x.capCents) || 0) / 100),
            }))
        : [];
      return { caps: merged, extras };
    }
  } catch {
    /* ignore */
  }
  return { caps: base, extras: [] };
}

function mapTxCategoryToBudgetKey(cat) {
  const c = String(cat || '').toLowerCase();
  if (/(vivi|alquil|rent|hipot|hogar|housing|mortgage)/.test(c)) return 'housing';
  if (/(comid|food|aliment|super|grocer)/.test(c)) return 'food';
  if (/(transport|uber|taxi|combust|nafta|auto|fuel)/.test(c)) return 'transport';
  if (/(salud|health|med|farma|doctor)/.test(c)) return 'health';
  if (/(educ|curso|capac|book|libro)/.test(c)) return 'education';
  if (/(serv|luz|gas|internet|agua|luz|wifi|utility)/.test(c)) return 'services';
  if (/(deud|loan|prést|prest|cuota|tarjeta|card)/.test(c)) return 'debts';
  if (/(ocio|entreten|netflix|spotify|leisure|fun)/.test(c)) return 'leisure';
  if (/(ahorr|savings|fondo|invers)/.test(c)) return 'savings';
  return 'other';
}

function PfMetricCard({ icon, label, value, hint, tone }) {
  const toneClass = tone ? ` pf-metric-card--tone-${tone}` : '';
  return (
    <div className={`pf-metric-card${toneClass}`}>
      <div className="pf-metric-card__top">
        <span className="pf-metric-card__icon" aria-hidden>
          {icon}
        </span>
        <span className="pf-metric-card__label">{label}</span>
      </div>
      <div className="pf-metric-card__value">{value}</div>
      {hint ? <div className="pf-metric-card__hint">{hint}</div> : <div className="pf-metric-card__hint pf-metric-card__hint--spacer" />}
    </div>
  );
}

export function PersonalFinance() {
  const { t } = useApp();
  const [summary, setSummary] = useState(null);
  const [txs, setTxs] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyTx);
  const [pfTab, setPfTab] = useState('summary');
  const [txFilterType, setTxFilterType] = useState('all');
  const [txFilterCat, setTxFilterCat] = useState('');
  const [txFilterMonth, setTxFilterMonth] = useState('');
  const [budgetModal, setBudgetModal] = useState(false);
  const [budgetCaps, setBudgetCaps] = useState(() => Object.fromEntries(BUDGET_KEYS.map((k) => [k, 0])));
  const [budgetExtras, setBudgetExtras] = useState([]);
  const [pfToast, setPfToast] = useState(null);
  const [showExtraModal, setShowExtraModal] = useState(false);
  const [extraForm, setExtraForm] = useState({ label: '', capPesos: 0 });
  const budgetHydrated = useRef(false);

  async function load() {
    setLoadFailed(false);
    setLoading(true);
    let s = null;
    let list = [];
    try {
      const res = await api.get('/api/personal-finance/summary');
      s = res.data;
    } catch (error) {
      console.error('PersonalFinance load error', error);
      setLoadFailed(true);
    }
    try {
      const res = await api.get('/api/personal-finance/transactions');
      list = Array.isArray(res.data) ? res.data : [];
    } catch (error) {
      console.error('PersonalFinance load error', error);
      setLoadFailed(true);
      list = [];
    }
    setSummary(s);
    setTxs(list);
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  const derivedSummary = useMemo(() => {
    if (summary) return null;
    let income = 0;
    let expense = 0;
    txs.forEach((tx) => {
      const a = Number(tx.amount_cents) || 0;
      if (tx.type === 'income') income += a;
      else expense += a;
    });
    const bal = income - expense;
    const sav = Math.max(0, bal);
    const ratio = income > 0 ? (income - expense) / income : 0;
    const score = Math.min(100, Math.max(30, Math.round(52 + ratio * 48)));
    return {
      income_month_cents: income,
      expense_month_cents: expense,
      estimated_balance_cents: bal,
      estimated_savings_cents: sav,
      financial_health_score: score,
    };
  }, [summary, txs]);

  const s = summary || derivedSummary;

  const income = s?.income_month_cents ?? 0;
  const expense = s?.expense_month_cents ?? 0;
  const balance = s?.estimated_balance_cents ?? 0;
  const savings = s?.estimated_savings_cents ?? 0;
  const score = s?.financial_health_score ?? 0;

  useEffect(() => {
    if (!s || income <= 0 || budgetHydrated.current) return;
    budgetHydrated.current = true;
    const { caps, extras } = loadBudgetFromStorage(income);
    setBudgetCaps(caps);
    setBudgetExtras(extras);
  }, [s, income]);

  useEffect(() => {
    if (!budgetHydrated.current || !s || income <= 0) return;
    try {
      localStorage.setItem(PF_BUDGET_LS, JSON.stringify({ version: 3, caps: budgetCaps, extras: budgetExtras }));
    } catch {
      /* ignore */
    }
  }, [budgetCaps, budgetExtras, s, income]);

  useEffect(() => {
    if (!pfToast) return undefined;
    const id = window.setTimeout(() => setPfToast(null), 2600);
    return () => window.clearTimeout(id);
  }, [pfToast]);

  const spentByBudgetKey = useMemo(() => {
    const m = Object.fromEntries(BUDGET_KEYS.map((k) => [k, 0]));
    txs.forEach((tx) => {
      if (tx.type !== 'expense') return;
      const k = mapTxCategoryToBudgetKey(tx.category);
      m[k] = (m[k] || 0) + (Number(tx.amount_cents) || 0);
    });
    return m;
  }, [txs]);

  const maxBar = Math.max(income, expense, 1);
  const incomePct = Math.round((100 * income) / maxBar);
  const expensePct = Math.round((100 * expense) / maxBar);
  const debtCents = expense > income ? expense - income : 0;
  const upcoming = txs.slice(0, 4);

  const txMonths = useMemo(() => {
    const set = new Set();
    txs.forEach((tx) => {
      const d = tx.transaction_date;
      if (d && d.length >= 7) set.add(d.slice(0, 7));
    });
    return Array.from(set).sort().reverse();
  }, [txs]);

  const filteredTxs = useMemo(() => {
    return txs.filter((tx) => {
      if (txFilterType === 'income' && tx.type !== 'income') return false;
      if (txFilterType === 'expense' && tx.type !== 'expense') return false;
      if (txFilterCat.trim()) {
        const c = String(tx.category || '').toLowerCase();
        if (!c.includes(txFilterCat.trim().toLowerCase())) return false;
      }
      if (txFilterMonth && String(tx.transaction_date || '').slice(0, 7) !== txFilterMonth) return false;
      return true;
    });
  }, [txs, txFilterType, txFilterCat, txFilterMonth]);

  const demoGoals = useMemo(
    () => [
      {
        id: 'g1',
        titleKey: 'goalEmergency',
        target: 90000000,
        saved: 22000000,
        due: '2026-12-31',
        monthly: 2800000,
        status: 'on',
      },
      {
        id: 'g2',
        titleKey: 'goalVacation',
        target: 45000000,
        saved: 12000000,
        due: '2026-08-15',
        monthly: 2100000,
        status: 'on',
      },
      {
        id: 'g3',
        titleKey: 'goalEducation',
        target: 30000000,
        saved: 8000000,
        due: '2026-11-01',
        monthly: 1800000,
        status: 'risk',
      },
      {
        id: 'g4',
        titleKey: 'pfDemoGoalPurchase',
        target: 120000000,
        saved: 15000000,
        due: '2027-03-01',
        monthly: 4500000,
        status: 'on',
      },
      {
        id: 'g5',
        titleKey: 'goalDebt',
        target: 25000000,
        saved: 25000000,
        due: '2026-04-01',
        monthly: 0,
        status: 'done',
      },
    ],
    []
  );

  const demoDebts = useMemo(
    () => [
      {
        id: 'd1',
        name: 'Tarjeta · consumo',
        balance: 18500000,
        fee: 920000,
        rate: '48%',
        due: '2026-05-18',
        progress: 42,
        priority: 'high',
      },
      {
        id: 'd2',
        name: 'Préstamo personal',
        balance: 42000000,
        fee: 1100000,
        rate: '32%',
        due: '2026-06-02',
        progress: 58,
        priority: 'medium',
      },
    ],
    []
  );

  const recurringIncome = useMemo(
    () => [
      { labelKey: 'pfRecur_salary', amount: Math.round(income * 0.72) || 80000000 },
      { labelKey: 'pfRecur_extra', amount: Math.round(income * 0.18) || 15000000 },
      { labelKey: 'pfRecur_freelance', amount: Math.max(0, income - Math.round(income * 0.9)) || 8000000 },
    ],
    [income]
  );

  const recurringExpense = useMemo(
    () => [
      { labelKey: 'pfRecur_rent', amount: Math.round(expense * 0.35) || 35000000 },
      { labelKey: 'pfRecur_utilities', amount: Math.round(expense * 0.12) || 9000000 },
      { labelKey: 'pfRecur_subs', amount: 4200000 },
      { labelKey: 'pfRecur_loan', amount: Math.round(expense * 0.15) || 6000000 },
      { labelKey: 'pfRecur_insurance', amount: 3100000 },
    ],
    [expense]
  );

  const fixedIn = useMemo(() => recurringIncome.reduce((a, r) => a + r.amount, 0), [recurringIncome]);
  const fixedOut = useMemo(() => recurringExpense.reduce((a, r) => a + r.amount, 0), [recurringExpense]);
  const marginRecur = fixedIn - fixedOut;

  const split5030 = useMemo(() => {
    const needSpent =
      (spentByBudgetKey.housing || 0) +
      (spentByBudgetKey.food || 0) +
      (spentByBudgetKey.transport || 0) +
      (spentByBudgetKey.health || 0) +
      (spentByBudgetKey.services || 0) +
      (spentByBudgetKey.debts || 0) * 0.5;
    const wantSpent = (spentByBudgetKey.leisure || 0) + (spentByBudgetKey.education || 0) * 0.5 + (spentByBudgetKey.debts || 0) * 0.5;
    const saveSpent = (spentByBudgetKey.savings || 0) + Math.max(0, savings);
    const tot = needSpent + wantSpent + saveSpent || 1;
    const need = Math.round((100 * needSpent) / tot);
    const want = Math.round((100 * wantSpent) / tot);
    const save = Math.max(0, 100 - need - want);
    return { need, want, save };
  }, [spentByBudgetKey, savings]);

  const alerts = useMemo(() => {
    const list = [];
    if (income > 0 && expense / income > 0.85) list.push({ key: 'high', text: t('personalFinance.pfAlertHighSpend') });
    if (income > 0 && savings / income < 0.05) list.push({ key: 'lows', text: t('personalFinance.pfAlertLowSave') });
    if (expense > income) list.push({ key: 'over', text: t('personalFinance.pfAlertOverBudget') });
    const soon = demoDebts.some((d) => {
      const dueMs = new Date(d.due).getTime();
      return dueMs - Date.now() < 14 * 86400000 && dueMs > Date.now();
    });
    if (soon) list.push({ key: 'due', text: t('personalFinance.pfAlertDebtDue') });
    return list;
  }, [income, expense, savings, t, demoDebts]);

  async function add() {
    try {
      await api.post('/api/personal-finance/transactions', {
        ...form,
        amount_cents: Math.round(Number(form.amount_cents)),
      });
      setModal(false);
      setForm(emptyTx);
      await load();
    } catch (error) {
      console.error('PersonalFinance save error', error);
    }
  }

  function openIncomeModal() {
    setForm({ ...emptyTx, type: 'income', transaction_date: new Date().toISOString().slice(0, 10) });
    setModal(true);
  }

  function openExpenseModal() {
    setForm({ ...emptyTx, type: 'expense', transaction_date: new Date().toISOString().slice(0, 10) });
    setModal(true);
  }

  const showGlobalEmpty = loadFailed && !summary && txs.length === 0;
  const showTabs = !loading && !showGlobalEmpty;

  const totalBudgetCap = useMemo(
    () =>
      BUDGET_KEYS.reduce((a, k) => a + (budgetCaps[k] || 0), 0) +
      budgetExtras.reduce((a, x) => a + (Number(x.capPesos) || 0), 0),
    [budgetCaps, budgetExtras]
  );

  const projectionRows = useMemo(() => {
    const surplus = income - expense;
    const scenarios = [
      { id: 'cons', mul: 0.75, label: t('personalFinance.pfProjScenarioCons') },
      { id: 'base', mul: 1, label: t('personalFinance.pfProjScenarioBase') },
      { id: 'opt', mul: 1.2, label: t('personalFinance.pfProjScenarioOpt') },
    ];
    return scenarios.map((sc) => {
      const rows = [];
      let bal = balance;
      let accSave = savings;
      for (let m = 1; m <= 3; m += 1) {
        const flow = Math.round(surplus * sc.mul);
        bal += flow;
        accSave += Math.max(0, flow);
        rows.push({ m, bal, accSave, deficit: bal < 0 });
      }
      return { ...sc, rows };
    });
  }, [balance, income, expense, savings, t]);

  return (
    <div className="page-stack page-stack--pf page-stack--pf-v15">
      {pfToast ? <div className="app-toast app-toast--ok">{pfToast}</div> : null}
      <section className="pf-page-hero">
        <div className="pf-page-hero__text">
          <h1 className="pf-page-hero__title">{t('personalFinance.heroTitle')}</h1>
          <p className="pf-page-hero__lead">{t('personalFinance.heroLead')}</p>
        </div>
      </section>

      <div className="privacy-callout privacy-callout--v15 privacy-callout--primary" role="status">
        <span className="privacy-callout__icon" aria-hidden>
          🔒
        </span>
        <div>
          <p className="privacy-callout__text">{t('personalFinance.privacyBannerMain')}</p>
          <details className="privacy-callout__details">
            <summary>{t('personalFinance.privacyLearnMore')}</summary>
            <p className="privacy-callout__sub">{t('personalFinance.privacyLearnBody')}</p>
          </details>
        </div>
      </div>

      {loading && (
        <div className="empty-state empty-state--premium">
          <div className="skeleton-line skeleton-line--lg" />
          <div className="skeleton-line" />
          <p className="panel__muted">{t('personalFinance.loading')}</p>
        </div>
      )}

      {!loading && showGlobalEmpty && (
        <div className="empty-state empty-state--premium empty-state--boxed">
          <span className="empty-state__icon" aria-hidden>
            ◎
          </span>
          <p className="empty-state__title">{t('personalFinance.emptySummaryTitle')}</p>
          <p className="empty-state__desc">{t('personalFinance.empty')}</p>
        </div>
      )}

      {showTabs && (
        <>
          <div className="pf-pro-tabs-wrap">
            <div className="pf-pro-tabs" role="tablist" aria-label={t('personalFinance.title')}>
              {PF_TAB_IDS.map((id) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={pfTab === id}
                  className={`pf-pro-tab${pfTab === id ? ' is-active' : ''}`}
                  onClick={() => setPfTab(id)}
                >
                  {t(`personalFinance.${PF_TAB_I18N[id]}`)}
                </button>
              ))}
            </div>
          </div>

          <p className="panel__muted text-sm pf-pro-hint">{t('personalFinance.pfSecureHint')}</p>

          {pfTab === 'summary' && s && (
            <>
              <section className="pf-top-grid">
                <div className="pf-wellbeing-card panel panel--elevated">
                  <h2 className="pf-wellbeing-card__title">{t('personalFinance.wellbeingTitle')}</h2>
                  <p className="pf-wellbeing-card__sub">{t('personalFinance.wellbeingSubtitle')}</p>
                  <div className="pf-health-ring-wrap pf-health-ring-wrap--large" aria-label={t('personalFinance.pfScoreTitle')}>
                    <div className="pf-health-ring" style={{ '--pf-score': Math.min(100, Math.max(0, score)) }}>
                      <div className="pf-health-ring__inner">
                        <span className="pf-health-ring__value">{Math.round(score)}</span>
                        <span className="pf-health-ring__label">{t('personalFinance.healthScore')}</span>
                      </div>
                    </div>
                  </div>
                  <span className="pf-chip pf-chip--soft">{t('personalFinance.pfPrivacyRepeat')}</span>
                </div>

                <div className="pf-metrics-grid">
                  <PfMetricCard
                    icon="◎"
                    tone="accent"
                    label={t('personalFinance.metricBalanceMonth')}
                    value={formatCents(balance, 'ARS')}
                    hint={t('personalFinance.balance')}
                  />
                  <PfMetricCard
                    icon="↑"
                    tone="success"
                    label={t('personalFinance.metricIncomeApprox')}
                    value={formatCents(income, 'ARS')}
                    hint={t('personalFinance.incomeMonth')}
                  />
                  <PfMetricCard
                    icon="↓"
                    tone="warning"
                    label={t('personalFinance.metricExpenseApprox')}
                    value={formatCents(expense, 'ARS')}
                    hint={t('personalFinance.expenseMonth')}
                  />
                  <PfMetricCard
                    icon="◆"
                    label={t('personalFinance.metricSavingsApprox')}
                    value={formatCents(savings, 'ARS')}
                    hint={t('personalFinance.savings')}
                  />
                  <PfMetricCard
                    icon="⚠"
                    tone={debtCents > 0 ? 'danger' : 'default'}
                    label={t('personalFinance.metricDebtGap')}
                    value={formatCents(debtCents, 'ARS')}
                    hint={debtCents > 0 ? t('personalFinance.debt') : t('personalFinance.summarySavingsLine')}
                  />
                </div>
              </section>

              <section className="panel panel--elevated pf-split-panel">
                <h3 className="panel__title">{t('personalFinance.pfDistTitle')}</h3>
                <div className="pf-split-bars">
                  <div>
                    <span className="panel__muted text-sm">{t('personalFinance.pfDistNeeds')}</span>
                    <div className="pf-split-bar">
                      <div className="pf-split-bar__fill pf-split-bar__fill--need" style={{ width: `${split5030.need}%` }} />
                    </div>
                    <span className="pf-split-val">{split5030.need}%</span>
                  </div>
                  <div>
                    <span className="panel__muted text-sm">{t('personalFinance.pfDistWants')}</span>
                    <div className="pf-split-bar">
                      <div className="pf-split-bar__fill pf-split-bar__fill--want" style={{ width: `${split5030.want}%` }} />
                    </div>
                    <span className="pf-split-val">{split5030.want}%</span>
                  </div>
                  <div>
                    <span className="panel__muted text-sm">{t('personalFinance.pfDistSaveDebt')}</span>
                    <div className="pf-split-bar">
                      <div className="pf-split-bar__fill pf-split-bar__fill--save" style={{ width: `${split5030.save}%` }} />
                    </div>
                    <span className="pf-split-val">{split5030.save}%</span>
                  </div>
                </div>
              </section>

              {alerts.length > 0 && (
                <section className="panel panel--elevated pf-alerts-panel">
                  <h3 className="panel__title">{t('personalFinance.pfAlertsTitle')}</h3>
                  <ul className="pf-alert-list">
                    {alerts.map((a) => (
                      <li key={a.key} className="pf-alert-list__item">
                        {a.text}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <section className="panel panel--elevated pf-summary-panel">
                <h2 className="panel__title">{t('personalFinance.summaryTitle')}</h2>
                <p className="panel__muted pf-summary-panel__intro">{t('personalFinance.summaryIntro')}</p>

                <div className="pf-summary-grid">
                  <div className="pf-summary-item">
                    <span className="pf-summary-item__label">{t('personalFinance.summaryHealth')}</span>
                    <strong className="pf-summary-item__value">{Math.round(score)}/100</strong>
                  </div>
                  <div className="pf-summary-item">
                    <span className="pf-summary-item__label">{t('personalFinance.summaryBalance')}</span>
                    <strong className="pf-summary-item__value">{formatCents(balance, 'ARS')}</strong>
                  </div>
                  <div className="pf-summary-item pf-summary-item--wide">
                    <span className="pf-summary-item__label">{t('personalFinance.summaryCompare')}</span>
                    <div className="pf-bar-compare">
                      <div className="pf-bar-compare__track">
                        <div
                          className="pf-bar-compare__fill pf-bar-compare__fill--income"
                          style={{ width: `${incomePct}%` }}
                          title={t('personalFinance.barIncome')}
                        />
                      </div>
                      <div className="pf-bar-compare__track">
                        <div
                          className="pf-bar-compare__fill pf-bar-compare__fill--expense"
                          style={{ width: `${expensePct}%` }}
                          title={t('personalFinance.barExpense')}
                        />
                      </div>
                    </div>
                    <div className="pf-bar-compare__legend">
                      <span>{t('personalFinance.barIncome')}</span>
                      <span>{t('personalFinance.barExpense')}</span>
                    </div>
                  </div>
                  <div className="pf-summary-item">
                    <span className="pf-summary-item__label">{t('personalFinance.summarySavingsLine')}</span>
                    <strong className="pf-summary-item__value">{formatCents(savings, 'ARS')}</strong>
                  </div>
                </div>

                <div className="pf-next-block">
                  <h3 className="pf-next-block__title">{t('personalFinance.summaryNextTitle')}</h3>
                  {upcoming.length === 0 ? (
                    <p className="panel__muted">{t('personalFinance.emptySummaryDesc')}</p>
                  ) : (
                    <ul className="pf-next-list">
                      {upcoming.map((tx) => (
                        <li key={tx.id} className="pf-next-list__row">
                          <Badge variant={tx.type === 'income' ? 'approved' : 'pending'}>{tx.type}</Badge>
                          <span className="pf-next-list__meta">{formatDate(tx.transaction_date)}</span>
                          <span className="pf-next-list__amt">{formatCents(tx.amount_cents ?? 0, tx.currency || 'ARS')}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <ul className="pf-reco-list">
                    <li>{t('personalFinance.tip1')}</li>
                    <li>{t('personalFinance.tip2')}</li>
                  </ul>
                </div>
              </section>

              <div className="grid-2 pf-pro-grid">
                <section className="panel panel--elevated">
                  <h2 className="panel__title">{t('personalFinance.sectionBudgetTitle')}</h2>
                  <p className="panel__muted text-sm">{t('personalFinance.budgetDemoHint')}</p>
                  <div className="pf-budget-bar">
                    <div className="pf-budget-bar__fill" style={{ width: `${Math.min(100, expensePct)}%` }} />
                  </div>
                  <div className="pf-budget-legend">
                    <span>
                      {t('personalFinance.budgetUsed')}: <strong>{formatCents(expense, 'ARS')}</strong>
                    </span>
                    <span>
                      {t('personalFinance.budgetAvailable')}: <strong>{formatCents(Math.max(0, income - expense), 'ARS')}</strong>
                    </span>
                  </div>
                  {expense > income ? <p className="banner banner--info pf-budget-warn">{t('personalFinance.budgetExceeded')}</p> : null}
                </section>
                <section className="panel panel--elevated">
                  <h2 className="panel__title">{t('personalFinance.goalsDetailTitle')}</h2>
                  <ul className="pi-card-list">
                    <li>{t('personalFinance.goalEmergency')}</li>
                    <li>{t('personalFinance.goalVacation')}</li>
                    <li>{t('personalFinance.goalEducation')}</li>
                    <li>{t('personalFinance.goalDebt')}</li>
                  </ul>
                </section>
              </div>

              <section className="panel panel--elevated pf-reco-pro">
                <h2 className="panel__title">{t('personalFinance.recoBlockTitle')}</h2>
                <ul className="pi-card-list">
                  <li>{t('personalFinance.reco1')}</li>
                  <li>{t('personalFinance.reco2')}</li>
                  <li>{t('personalFinance.reco3')}</li>
                </ul>
                <p className="panel__muted text-sm" style={{ marginTop: '0.75rem' }}>
                  {t('personalFinance.recurringIn')} · {t('personalFinance.recurringOut')} · {t('personalFinance.categorySplit')}
                </p>
              </section>

              <div className="grid-2">
                <section className="panel panel--elevated">
                  <h3 className="panel__title">{t('personalFinance.tipsTitle')}</h3>
                  <ul className="pf-tips-list">
                    <li>{t('personalFinance.tip1')}</li>
                    <li>{t('personalFinance.tip2')}</li>
                    <li>{t('personalFinance.tip3')}</li>
                  </ul>
                </section>
                <section className="panel panel--elevated">
                  <h3 className="panel__title">{t('personalFinance.goalsTitle')}</h3>
                  <p className="panel__muted">{t('personalFinance.goalPlaceholder')}</p>
                </section>
              </div>

              <section className="panel panel--pf-roadmap">
                <h3 className="panel__title">{t('personalFinance.roadmapTitle')}</h3>
                <p className="panel__muted">{t('personalFinance.roadmapBody')}</p>
              </section>
            </>
          )}

          {pfTab === 'summary' && !s && (
            <div className="panel panel--elevated">
              <p className="panel__muted">{t('personalFinance.emptySummaryDesc')}</p>
            </div>
          )}

          {pfTab === 'budget' && (
            <div className="pf-tab-stack">
              <section className="panel panel--elevated">
                <div className="pf-budget-head">
                  <div>
                    <h2 className="panel__title">{t('personalFinance.pfTabBudget')}</h2>
                    <p className="panel__muted text-sm">{t('personalFinance.pfBudgetMonthlyTotal')}</p>
                    <p className="pf-budget-total mono">{formatCurrencyARS(totalBudgetCap)}</p>
                    <p className="panel__muted text-sm" style={{ marginTop: '0.35rem' }}>
                      {t('personalFinance.pfBudgetPersistHint')}
                    </p>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button type="button" className="btn btn--secondary btn--sm" onClick={() => setShowExtraModal(true)}>
                      {t('personalFinance.pfBudgetAddCategory')}
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => {
                        if (!window.confirm(t('personalFinance.pfBudgetResetConfirm'))) return;
                        setBudgetExtras([]);
                        setBudgetCaps(defaultBudgetCapsFromIncome(income));
                        setPfToast(t('personalFinance.pfBudgetResetDone'));
                      }}
                    >
                      {t('personalFinance.pfBudgetResetBtn')}
                    </button>
                    <button type="button" className="btn btn--secondary" onClick={() => setBudgetModal(true)}>
                      {t('personalFinance.pfCreateBudget')}
                    </button>
                  </div>
                </div>
                <div className="table-wrap table-wrap--rounded table-wrap--pf">
                  <table className="data-table data-table--pf">
                    <thead>
                      <tr>
                        <th>{t('personalFinance.pfColCategory')}</th>
                        <th>{t('personalFinance.pfColBudgeted')}</th>
                        <th>{t('personalFinance.pfColSpent')}</th>
                        <th>{t('personalFinance.pfColAvailable')}</th>
                        <th>{t('personalFinance.pfColPct')}</th>
                        <th>{t('personalFinance.pfColStatus')}</th>
                        <th>{t('personalFinance.pfColActions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {BUDGET_KEYS.map((k) => {
                        const cap = budgetCaps[k] || 0;
                        const spentCents = spentByBudgetKey[k] || 0;
                        const spentPesos = Math.round(spentCents / 100);
                        const avail = cap - spentPesos;
                        const pct = cap > 0 ? Math.round((100 * spentPesos) / cap) : 0;
                        let st = t('personalFinance.pfStatusOk');
                        let stClass = 'pf-budget-status--ok';
                        if (pct > 100) {
                          st = t('personalFinance.pfStatusOver');
                          stClass = 'pf-budget-status--over';
                        } else if (pct > 85) {
                          st = t('personalFinance.pfStatusWarn');
                          stClass = 'pf-budget-status--warn';
                        }
                        return (
                          <tr key={k}>
                            <td>{t(`personalFinance.pfCat_${k}`)}</td>
                            <td>
                              <input
                                type="number"
                                className="input input--sm"
                                style={{ maxWidth: '9rem' }}
                                value={cap}
                                min={0}
                                onChange={(e) =>
                                  setBudgetCaps((prev) => ({
                                    ...prev,
                                    [k]: Math.max(0, Math.round(Number(e.target.value) || 0)),
                                  }))
                                }
                                aria-label={t('personalFinance.pfBudgetFieldArs')}
                              />
                            </td>
                            <td>{formatCurrencyARS(spentPesos)}</td>
                            <td>{formatCurrencyARS(avail)}</td>
                            <td>{pct}%</td>
                            <td>
                              <span className={`pf-budget-status ${stClass}`}>{st}</span>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                onClick={() => {
                                  setBudgetCaps((prev) => ({
                                    ...prev,
                                    [k]: defaultBudgetCapsFromIncome(income)[k] || 0,
                                  }));
                                  setPfToast(t('personalFinance.pfBudgetToastSaved'));
                                }}
                              >
                                {t('personalFinance.pfBudgetEditCap')}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                      {budgetExtras.map((x) => {
                        const cap = Number(x.capPesos) || 0;
                        const spentPesos = 0;
                        const avail = cap - spentPesos;
                        const pct = 0;
                        return (
                          <tr key={x.id}>
                            <td>{x.label}</td>
                            <td>
                              <input
                                type="number"
                                className="input input--sm"
                                style={{ maxWidth: '9rem' }}
                                value={cap}
                                min={0}
                                onChange={(e) => {
                                  const v = Math.max(0, Math.round(Number(e.target.value) || 0));
                                  setBudgetExtras((prev) => prev.map((row) => (row.id === x.id ? { ...row, capPesos: v } : row)));
                                }}
                                aria-label={t('personalFinance.pfBudgetFieldArs')}
                              />
                            </td>
                            <td>{formatCurrencyARS(spentPesos)}</td>
                            <td>{formatCurrencyARS(avail)}</td>
                            <td>{pct}%</td>
                            <td>
                              <span className="pf-budget-status pf-budget-status--ok">{t('personalFinance.pfStatusOk')}</span>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                onClick={() => {
                                  if (!window.confirm(t('personalFinance.pfBudgetConfirmDeleteExtra'))) return;
                                  setBudgetExtras((prev) => prev.filter((row) => row.id !== x.id));
                                  setPfToast(t('personalFinance.pfBudgetToastSaved'));
                                }}
                              >
                                {t('personalFinance.pfBudgetDeleteRow')}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="panel panel--elevated pf-503020">
                <h3 className="panel__title">{t('personalFinance.pf503020Title')}</h3>
                <div className="pf-503020-grid">
                  <div>
                    <span className="panel__muted text-sm">{t('personalFinance.pf503020Need')}</span>
                    <div className="pf-503020-bar">
                      <div className="pf-503020-bar__ideal" style={{ width: '50%' }} title={t('personalFinance.pf503020Ideal')} />
                    </div>
                    <span className="text-sm">{t('personalFinance.pf503020Ideal')}: 50%</span>
                    <div className="pf-503020-bar pf-503020-bar--muted">
                      <div className="pf-503020-bar__yours" style={{ width: `${split5030.need}%` }} />
                    </div>
                    <span className="text-sm">
                      {t('personalFinance.pf503020Your')}: {split5030.need}%
                    </span>
                  </div>
                  <div>
                    <span className="panel__muted text-sm">{t('personalFinance.pf503020Want')}</span>
                    <div className="pf-503020-bar">
                      <div className="pf-503020-bar__ideal pf-503020-bar__ideal--want" style={{ width: '30%' }} />
                    </div>
                    <span className="text-sm">{t('personalFinance.pf503020Ideal')}: 30%</span>
                    <div className="pf-503020-bar pf-503020-bar--muted">
                      <div className="pf-503020-bar__yours pf-503020-bar__yours--want" style={{ width: `${split5030.want}%` }} />
                    </div>
                    <span className="text-sm">
                      {t('personalFinance.pf503020Your')}: {split5030.want}%
                    </span>
                  </div>
                  <div>
                    <span className="panel__muted text-sm">{t('personalFinance.pf503020Save')}</span>
                    <div className="pf-503020-bar">
                      <div className="pf-503020-bar__ideal pf-503020-bar__ideal--save" style={{ width: '20%' }} />
                    </div>
                    <span className="text-sm">{t('personalFinance.pf503020Ideal')}: 20%</span>
                    <div className="pf-503020-bar pf-503020-bar--muted">
                      <div className="pf-503020-bar__yours pf-503020-bar__yours--save" style={{ width: `${split5030.save}%` }} />
                    </div>
                    <span className="text-sm">
                      {t('personalFinance.pf503020Your')}: {split5030.save}%
                    </span>
                  </div>
                </div>
              </section>
            </div>
          )}

          {pfTab === 'movements' && (
            <div className="pf-tab-stack">
              <div className="toolbar toolbar--card pf-toolbar">
                <div className="pf-toolbar__filters">
                  <label className="pf-filter">
                    <span className="sr-only">{t('personalFinance.fieldType')}</span>
                    <select className="input input--sm" value={txFilterType} onChange={(e) => setTxFilterType(e.target.value)}>
                      <option value="all">{t('personalFinance.pfFilterAll')}</option>
                      <option value="income">{t('personalFinance.pfFilterIncome')}</option>
                      <option value="expense">{t('personalFinance.pfFilterExpense')}</option>
                    </select>
                  </label>
                  <label className="pf-filter">
                    <span className="sr-only">{t('personalFinance.pfFilterCategory')}</span>
                    <input
                      className="input input--sm"
                      placeholder={t('personalFinance.pfFilterCategory')}
                      value={txFilterCat}
                      onChange={(e) => setTxFilterCat(e.target.value)}
                    />
                  </label>
                  <label className="pf-filter">
                    <span className="sr-only">{t('personalFinance.pfFilterMonth')}</span>
                    <select className="input input--sm" value={txFilterMonth} onChange={(e) => setTxFilterMonth(e.target.value)}>
                      <option value="">{t('personalFinance.pfFilterMonthAll')}</option>
                      {txMonths.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="toolbar__grow" />
                <button type="button" className="btn btn--secondary btn--sm" onClick={openIncomeModal}>
                  {t('personalFinance.pfBtnLoadIncome')}
                </button>
                <button type="button" className="btn btn--primary btn--sm" onClick={openExpenseModal}>
                  {t('personalFinance.pfBtnLoadExpense')}
                </button>
              </div>

              <div className="table-wrap table-wrap--rounded table-wrap--pf">
                <table className="data-table data-table--pf data-table--pf-movements">
                  <thead>
                    <tr>
                      <th>{t('personalFinance.pfTableDate')}</th>
                      <th>{t('personalFinance.pfTableType')}</th>
                      <th>{t('personalFinance.pfTableCategory')}</th>
                      <th>{t('personalFinance.pfTableAmount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTxs.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="empty-inline empty-inline--premium">
                          <span className="empty-inline__icon" aria-hidden>
                            ⧗
                          </span>
                          {t('personalFinance.empty')}
                        </td>
                      </tr>
                    ) : (
                      filteredTxs.map((tx) => (
                        <tr key={tx.id}>
                          <td>{formatDate(tx.transaction_date)}</td>
                          <td>
                            <Badge variant={tx.type === 'income' ? 'approved' : 'pending'}>{enumLabel(t, 'personalFinance.txType', tx.type)}</Badge>
                          </td>
                          <td>{tx.category ?? '—'}</td>
                          <td className="pf-td-amount">{formatCents(tx.amount_cents ?? 0, tx.currency || 'ARS')}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {pfTab === 'goals' && (
            <section className="panel panel--elevated pf-goals-tab">
              <div className="pf-goals-head">
                <h2 className="panel__title">{t('personalFinance.pfTabGoals')}</h2>
                <button type="button" className="btn btn--secondary btn--sm">
                  {t('personalFinance.pfNewGoal')}
                </button>
              </div>
              <div className="pf-goal-card-grid">
                {demoGoals.map((g) => {
                  const pct = g.target > 0 ? Math.min(100, Math.round((100 * g.saved) / g.target)) : 0;
                  const left = Math.max(0, g.target - g.saved);
                  const monthsLeft = 8;
                  const sug = g.monthly || Math.round(left / Math.max(1, monthsLeft));
                  const statusLabel =
                    g.status === 'done'
                      ? t('personalFinance.pfGoalStatusDone')
                      : g.status === 'risk'
                        ? t('personalFinance.pfGoalStatusRisk')
                        : t('personalFinance.pfGoalStatusOn');
                  return (
                    <div key={g.id} className="pf-goal-card panel panel--elevated">
                      <h3 className="pf-goal-card__title">{t(`personalFinance.${g.titleKey}`)}</h3>
                      <div className="pf-goal-card__row">
                        <span>{t('personalFinance.pfGoalTarget')}</span>
                        <strong>{formatCents(g.target, 'ARS')}</strong>
                      </div>
                      <div className="pf-goal-card__row">
                        <span>{t('personalFinance.pfGoalSaved')}</span>
                        <strong>{formatCents(g.saved, 'ARS')}</strong>
                      </div>
                      <div className="pf-goal-card__meter">
                        <div className="pf-goal-card__meter-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="pf-goal-card__row">
                        <span>{t('personalFinance.pfGoalPct')}</span>
                        <strong>{pct}%</strong>
                      </div>
                      <div className="pf-goal-card__row">
                        <span>{t('personalFinance.pfGoalDue')}</span>
                        <span>{formatDate(g.due)}</span>
                      </div>
                      <div className="pf-goal-card__row">
                        <span>{t('personalFinance.pfGoalMonthly')}</span>
                        <strong>{formatCents(sug, 'ARS')}</strong>
                      </div>
                      <div className="pf-goal-card__row">
                        <span>{t('personalFinance.pfGoalStatus')}</span>
                        <Badge variant={g.status === 'done' ? 'approved' : g.status === 'risk' ? 'pending' : 'approved'}>{statusLabel}</Badge>
                      </div>
                      <p className="panel__muted text-sm pf-goal-card__insight">
                        {t('personalFinance.pfGoalInsight').replace('{amount}', formatCents(sug, 'ARS'))}
                      </p>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {pfTab === 'recurring' && (
            <div className="pf-tab-stack">
              <div className="grid-2">
                <section className="panel panel--elevated">
                  <h3 className="panel__title">{t('personalFinance.recurringIn')}</h3>
                  <ul className="pf-recur-list">
                    {recurringIncome.map((r) => (
                      <li key={r.labelKey} className="pf-recur-list__row">
                        <span>{t(`personalFinance.${r.labelKey}`)}</span>
                        <strong className="pf-td-amount">{formatCents(r.amount, 'ARS')}</strong>
                      </li>
                    ))}
                  </ul>
                </section>
                <section className="panel panel--elevated">
                  <h3 className="panel__title">{t('personalFinance.recurringOut')}</h3>
                  <ul className="pf-recur-list">
                    {recurringExpense.map((r) => (
                      <li key={r.labelKey} className="pf-recur-list__row">
                        <span>{t(`personalFinance.${r.labelKey}`)}</span>
                        <strong className="pf-td-amount">{formatCents(r.amount, 'ARS')}</strong>
                      </li>
                    ))}
                  </ul>
                </section>
              </div>
              <section className="panel panel--elevated pf-recur-kpis">
                <h3 className="panel__title">{t('personalFinance.categorySplit')}</h3>
                <div className="pf-kpi-row">
                  <div className="pf-kpi-pill">
                    <span>{t('personalFinance.pfRecurKpiIn')}</span>
                    <strong>{formatCents(fixedIn, 'ARS')}</strong>
                  </div>
                  <div className="pf-kpi-pill">
                    <span>{t('personalFinance.pfRecurKpiOut')}</span>
                    <strong>{formatCents(fixedOut, 'ARS')}</strong>
                  </div>
                  <div className="pf-kpi-pill">
                    <span>{t('personalFinance.pfRecurKpiMargin')}</span>
                    <strong>{formatCents(marginRecur, 'ARS')}</strong>
                  </div>
                  <div className="pf-kpi-pill">
                    <span>{t('personalFinance.pfRecurKpiSubs')}</span>
                    <strong>6</strong>
                  </div>
                </div>
                <h4 className="pf-subhead">{t('personalFinance.pfRecurAlertsTitle')}</h4>
                <ul className="pi-card-list">
                  {fixedOut / Math.max(1, fixedIn) > 0.82 ? <li>{t('personalFinance.pfRecurAlertHigh')}</li> : null}
                  <li>{t('personalFinance.pfRecurAlertSub')}</li>
                  <li>{t('personalFinance.pfRecurAlertDue')}</li>
                </ul>
              </section>
            </div>
          )}

          {pfTab === 'debts' && (
            <div className="pf-tab-stack">
              <div className="pf-debt-grid">
                {demoDebts.map((d) => (
                  <div key={d.id} className="pf-debt-card panel panel--elevated">
                    <h3 className="pf-debt-card__title">{d.name}</h3>
                    <div className="pf-debt-card__row">
                      <span>{t('personalFinance.debt')}</span>
                      <strong>{formatCents(d.balance, 'ARS')}</strong>
                    </div>
                    <div className="pf-debt-card__row">
                      <span>{t('personalFinance.pfDebtInstallment')}</span>
                      <strong>{formatCents(d.fee, 'ARS')}</strong>
                    </div>
                    <div className="pf-debt-card__row">
                      <span>{t('personalFinance.pfDebtRateLabel')}</span>
                      <span>{d.rate}</span>
                    </div>
                    <div className="pf-debt-card__row">
                      <span>{t('personalFinance.pfDebtDueLabel')}</span>
                      <span>{formatDate(d.due)}</span>
                    </div>
                    <div className="pf-debt-card__row">
                      <span>{t('personalFinance.pfDebtProgressLabel')}</span>
                      <span>{d.progress}%</span>
                    </div>
                    <div className="pf-debt-card__row">
                      <span>{t('personalFinance.pfDebtPriorityLabel')}</span>
                      <Badge variant={d.priority === 'high' ? 'error' : 'pending'}>{t(`personalFinance.pfDebtPri_${d.priority}`)}</Badge>
                    </div>
                    <div className="pf-debt-meter">
                      <div className="pf-debt-meter__fill" style={{ width: `${d.progress}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              <section className="panel panel--elevated">
                <h3 className="panel__title">{t('personalFinance.pfDebtStrategyTitle')}</h3>
                <ul className="pi-card-list">
                  <li>{t('personalFinance.pfDebtAvalanche')}</li>
                  <li>{t('personalFinance.pfDebtSnowball')}</li>
                </ul>
                <p className="panel__muted text-sm">{t('personalFinance.pfDebtHint')}</p>
              </section>
            </div>
          )}

          {pfTab === 'projection' && (
            <div className="pf-tab-stack">
              <section className="panel panel--elevated">
                <h2 className="panel__title">{t('personalFinance.pfProjTitle')}</h2>
                <div className="table-wrap table-wrap--rounded">
                  <table className="data-table data-table--pf">
                    <thead>
                      <tr>
                        <th>{t('personalFinance.pfProjScenarioCol')}</th>
                        <th>{t('personalFinance.pfProjMonthN').replace('{n}', '1')}</th>
                        <th>{t('personalFinance.pfProjMonthN').replace('{n}', '2')}</th>
                        <th>{t('personalFinance.pfProjMonthN').replace('{n}', '3')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projectionRows.map((pr) => (
                        <tr key={pr.id}>
                          <td>{pr.label}</td>
                          {pr.rows.map((r) => (
                            <td key={r.m}>
                              <div className="pf-proj-cell">
                                <span className="panel__muted text-sm">{t('personalFinance.pfProjBalance')}</span>
                                <strong className="pf-td-amount">{formatCents(r.bal, 'ARS')}</strong>
                                <span className="text-sm panel__muted">
                                  {t('personalFinance.pfProjDeficitRisk')}: {r.deficit ? '↑' : '—'}
                                </span>
                              </div>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="panel__muted text-sm" style={{ marginTop: '0.75rem' }}>
                  {t('personalFinance.pfProjSimSave').replace('{amount}', formatCents(500000, 'ARS'))} ·{' '}
                  {t('personalFinance.pfProjSimCut').replace('{pct}', '8')}
                </p>
              </section>
            </div>
          )}

          {pfTab === 'reco' && (
            <section className="panel panel--elevated pf-reco-mba">
              <h2 className="panel__title">{t('personalFinance.pfRecoMbaTitle')}</h2>
              <ol className="pf-mba-list">
                <li>{t('personalFinance.pfRecoMba1')}</li>
                <li>{t('personalFinance.pfRecoMba2')}</li>
                <li>{t('personalFinance.pfRecoMba3')}</li>
                <li>{t('personalFinance.pfRecoMba4')}</li>
                <li>{t('personalFinance.pfRecoMba5')}</li>
                <li>{t('personalFinance.pfRecoMba6')}</li>
                <li>{t('personalFinance.pfRecoMba7')}</li>
              </ol>
              <p className="panel__muted text-sm">{t('personalFinance.pfRecoFlowHint')}</p>
              <div className="banner banner--info" style={{ marginTop: '1rem' }}>
                {t('personalFinance.pfPrivacyModule')}
              </div>
              <p className="panel__muted text-sm" style={{ marginTop: '0.75rem' }}>
                {t('personalFinance.pfDisclaimerAdvice')}
              </p>
              <p className="panel__muted text-sm">{t('personalFinance.pfPrivacyAggregates')}</p>
            </section>
          )}
        </>
      )}

      {modal && (
        <div className="modal-backdrop" role="dialog" aria-modal>
          <div className="modal">
            <h2 className="modal__title">{form.type === 'income' ? t('personalFinance.newIncome') : t('personalFinance.newExpense')}</h2>
            <div className="form-grid">
              <label className="field">
                <span>{t('personalFinance.fieldType')}</span>
                <select className="input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  {PF_TX_TYPE_VALUES.map((tp) => (
                    <option key={tp} value={tp}>
                      {enumLabel(t, 'personalFinance.txType', tp)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>{t('personalFinance.fieldAmountCents')}</span>
                <input className="input" type="number" value={form.amount_cents} onChange={(e) => setForm({ ...form, amount_cents: e.target.value })} />
              </label>
              <label className="field">
                <span>{t('personalFinance.fieldCurrency')}</span>
                <select className="input" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
                  <option value="ARS">ARS</option>
                  <option value="USD">USD</option>
                </select>
              </label>
              <label className="field field--full">
                <span>{t('personalFinance.fieldCategory')}</span>
                <input className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              </label>
              <label className="field field--full">
                <span>{t('personalFinance.fieldDescription')}</span>
                <input className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </label>
              <label className="field">
                <span>{t('personalFinance.pfTableDate')}</span>
                <input className="input" type="date" value={form.transaction_date} onChange={(e) => setForm({ ...form, transaction_date: e.target.value })} />
              </label>
              <label className="field field--row">
                <input type="checkbox" checked={form.is_recurring} onChange={(e) => setForm({ ...form, is_recurring: e.target.checked })} />
                <span>Recurrente</span>
              </label>
            </div>
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setModal(false)}>
                {t('employees.cancel')}
              </button>
              <button type="button" className="btn btn--primary" onClick={add}>
                {t('employees.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showExtraModal && (
        <div className="modal-backdrop" role="dialog" aria-modal>
          <div className="modal">
            <h2 className="modal__title">{t('personalFinance.pfBudgetAddCategory')}</h2>
            <div className="form-grid">
              <label className="field field--full">
                <span>{t('personalFinance.pfBudgetExtraLabel')}</span>
                <input
                  className="input"
                  value={extraForm.label}
                  onChange={(e) => setExtraForm({ ...extraForm, label: e.target.value })}
                />
              </label>
              <label className="field field--full">
                <span>{t('personalFinance.pfBudgetExtraCap')}</span>
                <input
                  className="input"
                  type="number"
                  min={0}
                  value={extraForm.capPesos}
                  onChange={(e) =>
                    setExtraForm({ ...extraForm, capPesos: Math.max(0, Math.round(Number(e.target.value) || 0)) })
                  }
                />
              </label>
            </div>
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setShowExtraModal(false)}>
                {t('employees.cancel')}
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={!extraForm.label.trim()}
                onClick={() => {
                  const id = `x_${Date.now()}`;
                  setBudgetExtras((prev) => [...prev, { id, label: extraForm.label.trim(), capPesos: extraForm.capPesos }]);
                  setExtraForm({ label: '', capPesos: 0 });
                  setShowExtraModal(false);
                  setPfToast(t('personalFinance.pfBudgetToastSaved'));
                }}
              >
                {t('employees.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {budgetModal && (
        <div className="modal-backdrop" role="dialog" aria-modal>
          <div className="modal modal--wide">
            <h2 className="modal__title">{t('personalFinance.pfBudgetModalTitle')}</h2>
            <p className="panel__muted text-sm">{t('personalFinance.pfBudgetModalLead')}</p>
            <div className="pf-budget-modal-grid">
              {BUDGET_KEYS.map((k) => (
                <label key={k} className="field">
                  <span>{t(`personalFinance.pfCat_${k}`)} · {t('personalFinance.pfBudgetFieldArs')}</span>
                  <input
                    className="input"
                    type="number"
                    value={budgetCaps[k] || 0}
                    onChange={(e) => setBudgetCaps((prev) => ({ ...prev, [k]: Math.max(0, Math.round(Number(e.target.value) || 0)) }))}
                  />
                </label>
              ))}
            </div>
            <div className="modal__actions">
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => {
                  setBudgetModal(false);
                  setPfToast(t('personalFinance.pfBudgetToastSaved'));
                }}
              >
                {t('personalFinance.pfBudgetModalClose')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
